/**
 * CreateWorkspaceModal — "+ Add New Workspace" from the avatar menu.
 *
 * Collects the same details as the onboarding wizard's "Define your
 * workspace" step (logo and workspace name — the workspace name is the
 * company name, so no separate company field), calls `POST /workspaces`,
 * switches the session to the new workspace and reloads so every cached view
 * starts from the new context.  Any creator can open it — invitees are
 * promoted to creator once their invitation is fulfilled — and a refused
 * request surfaces as an error toast, except a name another workspace already
 * uses (409), which is shown under the name field.
 */
import React, { useEffect, useState } from 'react';

import { Modal, SparkModalPortal, sparkToast } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import { createWorkspace } from '../api/workspacesApi';
import { cropImageToSquare, logoImageErrorText } from '../utils/logoImage';
import useLogoCrop from '../hooks/useLogoCrop';
import LogoCropModal from './LogoCropModal';
import WorkspaceDetailsFields from './WorkspaceDetailsFields';
import '../styles/onboarding.css';
import './CreateWorkspaceModal.css';

export const CREATE_WORKSPACE_MODAL_WIDTH = 480;

const reloadPage = () => window.location.reload();

export default function CreateWorkspaceModal({
    open = false,
    onClose,
    cropLogo = cropImageToSquare,
    reload = reloadPage,
}) {
    const { switchWorkspace } = useAuth();
    const [name, setName] = useState('');
    const [logo, setLogo] = useState(null);
    const [nameError, setNameError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Picked file → crop dialog → `setLogo(dataUrl)`; a failure toasts and
    // keeps whatever logo was already chosen.
    const logoCrop = useLogoCrop({
        onLogo: setLogo,
        onError: (err) => sparkToast.error(logoImageErrorText(err)),
    });
    const { cancelCrop } = logoCrop;

    // Never leave the crop dialog orphaned on either transition, and start
    // every visit from a clean form.
    useEffect(() => {
        cancelCrop();
        if (!open) return;
        setName('');
        setLogo(null);
        setNameError('');
        setSubmitting(false);
    }, [open, cancelCrop]);

    const close = () => {
        if (submitting) return;
        onClose?.();
    };

    const submit = async () => {
        if (submitting) return;
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('Please enter a workspace name.');
            return;
        }
        setNameError('');
        setSubmitting(true);
        const result = await createWorkspace({ name: trimmed, logo });
        if (!result.ok) {
            setSubmitting(false);
            // A taken name is the user's to fix, so it belongs next to the field
            // rather than in a toast that disappears while they retype.
            if (result.nameTaken) setNameError(result.error);
            else sparkToast.error(result.error);
            return;
        }
        const created = result.data?.workspace;
        try {
            if (created?.id) await switchWorkspace(created.id);
        } catch (err) {
            // The backend already moved the session cookie to the new workspace;
            // a failed explicit switch only means the in-memory context is stale.
            setSubmitting(false);
            onClose?.();
            sparkToast.error(
                `Workspace "${created?.name || trimmed}" was created, but we could not open it. Please reload the page.`,
            );
            return;
        }
        reload();
    };

    return (
        <>
            <SparkModalPortal open={open} onClose={close} width={CREATE_WORKSPACE_MODAL_WIDTH} destroyOnClose>
                <Modal
                    open
                    className="create-workspace-modal"
                    title="Add New Workspace"
                    showSubtitle={false}
                    showOverlay={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    primaryLabel={submitting ? 'Creating…' : 'Create Workspace'}
                    primaryDisabled={submitting}
                    tertiaryLabel="Cancel"
                    tertiaryDisabled={submitting}
                    onPrimaryClick={submit}
                    onTertiaryClick={close}
                    onCloseClick={close}
                >
                    <form
                        className="create-workspace-modal__form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            submit();
                        }}
                    >
                        <WorkspaceDetailsFields
                            idPrefix="new-workspace"
                            size="medium"
                            logo={logo}
                            onLogoFile={logoCrop.pickLogo}
                            name={name}
                            onNameChange={(value) => {
                                setName(value);
                                if (nameError) setNameError('');
                            }}
                            nameError={nameError}
                            disabled={submitting}
                        />
                        {/* Lets Enter submit the form while the footer owns the visible buttons. */}
                        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
                    </form>
                </Modal>
            </SparkModalPortal>
            {/* Stacked above the form; kept outside it so its clicks never submit. */}
            <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />
        </>
    );
}
